import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import { uploadProfilePhoto } from '../services/api';
import { useAuth } from '../context/AuthContext';

function ProfileUpload() {
  const { user, setUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 16 * 1024 * 1024) {
        setError('File size must be less than 16MB');
        return;
      }

      if (!selectedFile.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a photo first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await uploadProfilePhoto(formData);
      setSuccess(`Photo uploaded successfully! Found ${response.matches_found} matching photos.`);

      // Update user in context and localStorage
      const updatedUser = { ...user, profile_photo: response.profile_photo };
      if (setUser) setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));

      setTimeout(() => {
        navigate('/my-photos');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="upload-container">
        <h2 style={{ textAlign: 'center', color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Upload Your Profile Photo
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '2rem' }}>
          Upload a clear photo of your face for AI facial recognition.
          We'll automatically find all photos where you appear!
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="upload-zone" onClick={() => document.getElementById('fileInput').click()}>
          {preview ? (
            <img src={preview} alt="Preview" className="preview-image" />
          ) : (
            <>
              <div className="upload-icon">
                <FaCloudUploadAlt />
              </div>
              <p>Click to select your photo</p>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                Supported formats: JPG, PNG (Max 16MB)
              </p>
            </>
          )}
        </div>

        <input
          id="fileInput"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {file && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ color: 'var(--gray)', marginBottom: '1rem' }}>
              Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </p>
            <button
              onClick={handleUpload}
              className="btn-submit"
              disabled={loading}
            >
              {loading ? 'Uploading and Processing...' : 'Upload & Find My Photos'}
            </button>
          </div>
        )}

        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'var(--off-white)',
          borderRadius: '12px',
          border: '2px solid var(--light-green)'
        }}>
          <h4 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
            <FaCheckCircle /> Tips for Best Results
          </h4>
          <ul style={{ color: 'var(--gray)', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>Use a clear, recent photo of your face</li>
            <li>Ensure good lighting and minimal shadows</li>
            <li>Face should be clearly visible and centered</li>
            <li>Avoid sunglasses or face coverings</li>
            <li>High-resolution photos work best</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ProfileUpload;
